import { useMemoize } from "@vueuse/core";

// JWT payload 类型定义
interface JWTPayload {
  batch_id: string;
  sub: string;
  callback_url?: string;
  company_id?: number;
  iat: number;
  exp: number;
}

// JWT验证响应类型
interface JWTVerifyResponse {
  success: boolean;
  message: string;
  payload?: JWTPayload;
  user_name?: string;
  error?: string;
}

// 全局响应式状态
const jwtPayload = ref<JWTPayload | null>(null);
const isJWTLoading = ref(false);
const jwtError = ref<string | null>(null);

// 验证JWT Token
const verifyJWT = useMemoize(
  async (token: string): Promise<JWTVerifyResponse> => {
    const { $myFetch } = useNuxtApp();
    
    return await ($myFetch as (url: string) => Promise<JWTVerifyResponse>)(`/api/verify-jwt?token=${encodeURIComponent(token)}`);
  }
);

// 从URL获取token并验证
const verifyTokenFromURL = async (): Promise<JWTPayload | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    isJWTLoading.value = true;
    jwtError.value = null;

    // 从URL参数获取token
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      jwtError.value = 'URL中未找到token参数';
      return null;
    }

    // 验证JWT token
    const response = await verifyJWT(token);
    
    if (response.success && response.payload) {
      jwtPayload.value = response.payload;
      return response.payload;
    } else {
      jwtError.value = response.message || 'JWT验证失败';
      return null;
    }
  } catch (err: unknown) {
    jwtError.value = (err as Error)?.message || 'JWT验证请求失败';
    console.error('JWT验证失败:', err);
    return null;
  } finally {
    isJWTLoading.value = false;
  }
};

// 检查token是否过期
const isTokenExpired = (payload: JWTPayload): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp <= currentTime;
};

// 检查token是否即将过期（5分钟内）
const isTokenExpiringSoon = (payload: JWTPayload): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  const fiveMinutes = 5 * 60;
  return payload.exp - currentTime <= fiveMinutes;
};

// 重置JWT状态
const resetJWTState = () => {
  jwtPayload.value = null;
  jwtError.value = null;
  isJWTLoading.value = false;
};

// 判断是否为SJK渠道
const isSJKChannel = (payload?: JWTPayload | null): boolean => {
  return !!(payload?.sub);
};

export function useJWT() {
  return {
    // 响应式状态
    jwtPayload: readonly(jwtPayload),
    isJWTLoading: readonly(isJWTLoading),
    jwtError: readonly(jwtError),
    
    // 方法
    verifyJWT,
    verifyTokenFromURL,
    isTokenExpired,
    isTokenExpiringSoon,
    resetJWTState,
    isSJKChannel,
  };
}
